import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabase';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      else setProfile(null);
    });

    return () => subscription.unsubscribe();
  }, []);

  async function fetchProfile(userId) {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    setProfile(data);
    return data;
  }

  async function signUp(email, password, fullName, userType = 'student') {
    // Pass user_type inside raw_user_meta_data so the DB trigger picks it up
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          user_type: userType,
        },
      },
    });

    if (!error && data?.user) {
      // The DB trigger creates the profile row using raw_user_meta_data,
      // but it fires asynchronously. Poll until the row exists, then
      // force-update user_type so there's no race condition.
      let attempts = 0;
      let profileRow = null;
      while (attempts < 8 && !profileRow) {
        await new Promise(r => setTimeout(r, 400));
        const { data: p } = await supabase
          .from('profiles')
          .select('id, user_type')
          .eq('id', data.user.id)
          .single();
        profileRow = p;
        attempts++;
      }

      // Always explicitly set user_type — this is the source of truth
      await supabase
        .from('profiles')
        .update({ full_name: fullName, user_type: userType })
        .eq('id', data.user.id);

      // Refresh profile in state with the correct user_type
      await fetchProfile(data.user.id);
    }

    return { data, error };
  }

  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  }

  async function signOut() {
    await supabase.auth.signOut();
    setProfile(null);
  }

  async function resetPassword(email) {
    return supabase.auth.resetPasswordForEmail(email);
  }

  const isTutor = profile?.user_type === 'tutor';
  const isStudent = !isTutor;

  return (
    <AuthContext.Provider value={{
      user, profile, loading,
      isTutor, isStudent,
      signUp, signIn, signOut, resetPassword, fetchProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
